import MailChecker from "mailchecker";
import { freeEmailDomains } from "./freeEmailDomains";
import { LEADING_FREE_PROVIDERS } from "./constants";
import { z } from "zod";

interface FormValidation<T> {
  touched: Partial<Record<keyof T, boolean>>;
  errors: Partial<Record<keyof T, string>>;
}

export const specialCharacters = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

const passwordValidator = z
  .string()
  .min(12, { message: "Password must be at least 12 characters long." })
  .refine((password) => /[A-Z]/.test(password), {
    message: "Password must include at least one uppercase letter.",
  })
  .refine((password) => /[a-z]/.test(password), {
    message: "Password must include at least one lowercase letter.",
  })
  .refine((password) => /[0-9]/.test(password), {
    message: "Password must include at least one number.",
  })
  .refine((password) => specialCharacters.test(password), {
    message: "Password must include at least one special character.",
  });

export const registrationSchema = z.object({
  name: z.string().min(1, "Please enter your name."),
  password: passwordValidator,
});

export const freeRegistrationSchema = z.object({
  company: z.string().min(1, "Please enter your instance name"),
  email: z.string().email("Please enter your email"),
  name: z.string().min(1, "Please enter your name"),
  password: passwordValidator,
});

export const passwordSchema = z.object({
  password: passwordValidator,
});

export const validateFormPassword = (
  password: string,
  setValidatePassword: (validPassword: number[]) => void
): void => {
  const validPassword = [1, 1, 1, 1, 1];

  if (password.length < 12) validPassword[0] = -1; // At least 12 characters
  if (!/[A-Z]/.test(password)) validPassword[1] = -1; // At least one uppercase letter
  if (!/[a-z]/.test(password)) validPassword[2] = -1; // At least one lowercase letter
  if (!/[0-9]/.test(password)) validPassword[3] = -1; // At least one number
  if (!specialCharacters.test(password)) validPassword[4] = -1; // At least one special character

  setValidatePassword(validPassword);
};

export const isFormFieldError =
  <T extends Record<string, unknown>>(v: FormValidation<T>) =>
  (key: keyof T): string | false | undefined =>
    v.touched[key] && v.errors[key];

export function isFreeEmailProvider(
  email: string,
  hasReferral = false
): boolean {
  if (email.includes("@")) {
    const domain = email.split("@")[1].toLowerCase();
    if (hasReferral && LEADING_FREE_PROVIDERS.has(domain)) return false;
    return freeEmailDomains.includes(domain);
  }
  return false;
}

export const validateEmail = (e: string, hasReferral = false): string => {
  if (!e) {
    return "";
  }
  if (isFreeEmailProvider(e, hasReferral)) {
    return "Please use a valid business email address";
  }
  if (!MailChecker.isValid(e)) {
    return "Please use a valid email address";
  }

  // Though single letter TLD is theoritically possible, it is not allowed by ICANN.
  // So we can safely assume that any email with single letter TLD is invalid.
  const [, tldName] = e.split("@")[1].split(".");
  if (tldName.length < 2) {
    return "Please use valid domain";
  }
  return "";
};
